/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { FindingsSummary, SystemAgentRunWithAgent } from '@shared/schema/systemAgents';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  getSystemAgentFindingsSummary,
  listSystemAgentRuns,
  formatRelativeTime,
  getRunStatusColor,
} from '../../lib/api/system-agents';
import {
  Bot,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowRight,
  Clock,
} from 'lucide-react';

interface SystemAgentStatusProps {
  isLoading?: boolean;
}

export function SystemAgentStatus({ isLoading: parentLoading }: SystemAgentStatusProps) {
  const [summary, setSummary] = useState<FindingsSummary | null>(null);
  const [recentRuns, setRecentRuns] = useState<SystemAgentRunWithAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [summaryResult, runsResult] = await Promise.all([
          getSystemAgentFindingsSummary({ status: 'open' }),
          listSystemAgentRuns({ limit: 5 }),
        ]);

        if (summaryResult.success && summaryResult.summary) {
          setSummary(summaryResult.summary);
        }
        if (runsResult.success && runsResult.runs) {
          setRecentRuns(runsResult.runs);
        }
      } catch (error) {
        console.error('Error fetching system agent status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const loading = parentLoading || isLoading;

  // Calculate stats
  const totalFindings = summary?.total || 0;
  const criticalCount = summary?.bySeverity?.critical || 0;
  const highCount = summary?.bySeverity?.high || 0;
  const warningCount = (summary?.bySeverity?.medium || 0) + (summary?.bySeverity?.low || 0);
  const runsLast24h = recentRuns.filter(r => {
    if (!r.createdAt) return false;
    const runTime = new Date(r.createdAt).getTime();
    const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
    return runTime > dayAgo;
  }).length;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Bot className="h-5 w-5" />
            System Agent Status
          </CardTitle>
          <Link to="/agents">
            <Button variant="ghost" size="sm" className="gap-1 text-xs">
              View All
              <ArrowRight className="h-3 w-3" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Stats */}
            <div className="text-sm text-muted-foreground">
              Last 24 hours: <span className="font-medium text-foreground">{runsLast24h} agent runs</span>
              {totalFindings > 0 && (
                <>
                  {' • '}
                  <span className="font-medium text-red-600">{totalFindings} findings requiring action</span>
                </>
              )}
            </div>

            {/* Severity Breakdown */}
            <div className="flex gap-3">
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 border border-red-200">
                <XCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm font-semibold text-red-700">{criticalCount + highCount}</span>
                <span className="text-xs text-red-600">Critical</span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-200">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className="text-sm font-semibold text-yellow-700">{warningCount}</span>
                <span className="text-xs text-yellow-600">Warning</span>
              </div>
            </div>

            {/* Recent Activity */}
            {recentRuns.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Recent Activity
                </h4>
                <div className="space-y-1.5">
                  {recentRuns.slice(0, 3).map((run) => (
                    <div
                      key={run.id}
                      className="flex items-center justify-between text-sm py-1.5 px-2 rounded bg-muted/50"
                    >
                      <div className="flex items-center gap-2">
                        {run.status === 'completed' && run.result === 'pass' && (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        )}
                        {run.status === 'completed' && run.result === 'warning' && (
                          <AlertTriangle className="h-4 w-4 text-yellow-500" />
                        )}
                        {run.status === 'completed' && run.result === 'fail' && (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                        {run.status === 'running' && (
                          <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                        )}
                        {run.status === 'failed' && (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                        {run.status === 'pending' && (
                          <Clock className="h-4 w-4 text-yellow-500" />
                        )}
                        <span className="truncate max-w-[150px]">
                          {run.agent?.displayName || run.agent?.name || `Agent ${run.agentId}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(run.createdAt!)}
                        </span>
                        <Badge className={`text-xs ${getRunStatusColor(run.status)}`}>
                          {run.result || run.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* View All Link */}
            <Link to="/agents" className="block">
              <Button variant="outline" className="w-full gap-2">
                View All Findings
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
